import React from 'react';
import {
    Responsive,
    SimpleList,
    Datagrid,
    TextField,
    BooleanField,
    SimpleForm,
    TextInput,
    SelectInput,
    required,
    minLength,
    maxLength,
    number
} from 'admin-on-rest';
import List from '../admin/List';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import EditButton from '../buttons/EditButtonByCustomKey';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/card-membership';
import {resolveAccess} from "../shared/authFunctions";

export const fuseVipCardConfigIcon = Icon;

const validateRanges = (values) => {
    const errors = {};
    const start = parseInt(values.range_start_string, 10);
    const end = parseInt(values.range_end_string, 10);
    if (!start) {
        errors.range_start_string = ['Start value must be a valid number.'];
    }
    if (!end) {
        errors.range_start_string = ['End value must be a valid number.'];
    }
    if (start >= end) {
        errors.range_end_string = ['End value must be higher than Start value.'];
    }
    return errors
};

/*
 * View: List
 */

// Set title for List VIP Card Configurations
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>VIP Card Configurations</span>;

const ListDescription = () => {
    return (
        <span>
            <p>A <strong>VIP Card</strong> represents a Loyalty Program account created either online or at a store
                Location's register via the POS.</p>
            <p>Loyalty Card Configurations consist of a seven-character prefix and a range of six-digit numbers. This
                becomes a set of available ID numbers for use as Loyalty accounts.</p>
        </span>
    );
};

export const fuseVipCardConfigList = (props) => (
    <List {...props} title={ListTitle} desc={<ListDescription/>} sort={{field: 'prefix', order: 'ASC'}}>
        {permissions =>
            <Responsive
                small={
                    <SimpleList
                        primaryText={record => record.prefix}
                        secondaryText={record => record.range_start + " - " + record.range_end}
                    />
                }
                medium={
                    <Datagrid bodyOptions={{stripedRows: false, showRowHover: true}}>
                        <TextField source="prefix" label="Card Prefix"/>
                        <TextField source="range_start_string" label="Range Start"/>
                        <TextField source="range_end_string" label="Range End"/>
                        <BooleanField source="active" label="Active" sortable={false}/>
                        {resolveAccess(permissions, ['loyalty_all']) &&
                        <EditButton customKey="prefix"/>
                        }
                    </Datagrid>
                }
            />
        }
    </List>
);

/*
 * View: Create form
 */

// Set title for Create VIP Card Configuration
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Create a VIP Card Configuration</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Create a VIP Card Configuration by creating a Prefix and entering a range of numbers to use as IDs. Once
                created, VIP Loyalty accounts can be created with this Prefix.</p>
            <p>A <strong>Prefix</strong> of VIP2018 and a range of 1 through 100000 would allow Loyalty
                accounts from VIP2018000001 through VIP2018100000.</p>
            <p>A Configuration may be limited. For instance, if you wanted a limited series of a thousand VIP
                accounts, you could create a Configuration of LIM2018000001 through LIM2018001000.</p>
        </span>
    );
};

const uppercaseLettersOnly = (value) => {
    if (value && /[a-z]/.test(value)) {
        return 'Prefix must be uppercase.';
    }
    return undefined;
};

/*
 * Create VIP Card Configuration form
 */
export const fuseVipCardConfigCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm redirect="list" validate={validateRanges}>
            <TextInput source="prefix" label="Card Prefix"
                       validate={[
                           required,
                           minLength(7, 'Prefix must be seven characters.'),
                           maxLength(7, 'Prefix must be seven characters.'),
                           uppercaseLettersOnly
                       ]}/>
            <span style={viewStyles.fieldDescription}>
                Enter a unique seven-character <strong>Card Prefix</strong>.
                <TooltipIcon dataFor="ttPrefix"/>
                <ReactTooltip id="ttPrefix" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Card Prefix</strong> will be the first seven characters of a Loyalty Account ID. This can
                    consist of any letters or numbers, such as three letters and a year or a string for testing
                    purposes (e.g., VIP2018, TEST001).
                    <br/><br/>
                    Prefix must be unique and contain only uppercase letters.
                </ReactTooltip>
            </span>

            <TextInput source="range_start_string" label="Range Start"
                       options={{'hintText': '000001'}}
                       validate={[
                           required,
                           number,
                           minLength(1, 'Must be at least 1.'),
                           maxLength(6, 'Maximum of six digits.')
                       ]}/>
            <span style={viewStyles.fieldDescription}>
                Enter a <strong>Start Range</strong> of up to six numbers (example: 1).
                <TooltipIcon dataFor="ttStart"/>
                <ReactTooltip id="ttStart" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The last six characters of a Loyalty Account ID are numbers.
                    The <strong>Start Range</strong> represents the beginning range of ID numbers available for this
                    Loyalty Prefix (e.g., VIP2018000001).
                    <br/><br/>
                    The Start Range can be anything below the End Range, down to 1. When an account is created, the
                    generated ID number will be six digits, adding zeroes as needed.
                    <br/><br/>
                    Once a Start Range and End Range are selected, Loyalty accounts will be available for every number
                    between the two ranges.
                </ReactTooltip>
            </span>

            <TextInput source="range_end_string" label="Range End"
                       options={{'hintText': '100000'}}
                       validate={[
                           required,
                           number,
                           minLength(1, 'Must be larger than the Start Range.'),
                           maxLength(6, 'Maximum of six digits.')
                       ]}/>
            <span style={viewStyles.fieldDescription}>
                Enter an <strong>End Range</strong> of six numbers (example: 100000).
                <br/><br/>
                The End Range must be larger than the Start Range.
                <TooltipIcon dataFor="ttEnd"/>
                <ReactTooltip id="ttEnd" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>End Range</strong> represents the limit of ID numbers available for this
                    Loyalty Prefix (e.g., VIP2018100000).
                    <br/><br/>
                    Once a Start Range and End Range are selected, Loyalty accounts will be available for every number
                    between and including the two ranges.
                </ReactTooltip>
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <SelectInput source="active" label="Card Configuration is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this series of Loyalty Account IDs may be applied on the POS System.
                    <br/>
                    If <em>Inactive</em>, this series of Loyalty Account IDs may not be applied on the POS System.
                    <br/><br/>
                    Setting a Configuration to inactive allows you to create Loyalty Accounts that, for instance,
                    are valid for a limited time.
                </ReactTooltip>
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the VIP Card Configurations List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit VIP Card Configuration
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.prefix}` : 'VIP Card Configuration'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify number ranges or active status below. Click <strong>Save</strong> to store this VIP Card Configuration.</p>
        </span>
    );
};

/*
 * Edit VIP Card Configuration form
 */
export const fuseVipCardConfigEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm validate={validateRanges}>
            <TextField source="prefix" label="Card Prefix (cannot be edited)"/>
            <span style={viewStyles.fieldDescription}>
                The unique seven-character <strong>Card Prefix</strong>.
                <TooltipIcon dataFor="ttPrefix"/>
                <ReactTooltip id="ttPrefix" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    A <strong>Card Prefix</strong> is the first seven characters of a Loyalty Account ID. This
                    consists of letters and numbers, such as three letters and a year or a string for testing
                    purposes (e.g., VIP2018, TEST001).
                </ReactTooltip>
            </span>

            <TextInput source="range_start_string" label="Range Start"
                       validate={[
                           required,
                           number,
                           minLength(1, 'Must be at least 1.'),
                           maxLength(6, 'Maximum of six digits.')
                       ]}/>
            <span style={viewStyles.fieldDescription}>
                Modify the <strong>Start Range</strong> up to six digits.
                <TooltipIcon dataFor="ttStart"/>
                <ReactTooltip id="ttStart" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The last six characters of a Loyalty Account ID are numbers.
                    The <strong>Start Range</strong> represents the beginning range of ID numbers available for this
                    Loyalty Prefix (e.g., VIP2018000001).
                    <br/><br/>
                    The Start Range can be anything below the End Range, down to 1. When an account is created, the
                    generated ID number will be six digits, adding zeroes as needed.
                </ReactTooltip>
            </span>

            <TextInput source="range_end_string" label="Range End"
                       validate={[
                           required,
                           number,
                           minLength(1, 'Must be larger than the Start Range.'),
                           maxLength(6, 'Maximum of six digits.')
                       ]}/>
            <span style={viewStyles.fieldDescription}>
                Modify the <strong>End Range</strong> up to six digits.
                <br/><br/>
                The End Range must be larger than the Start Range.
                <TooltipIcon dataFor="ttEnd"/>
                <ReactTooltip id="ttEnd" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    The <strong>End Range</strong> represents the limit of ID numbers available for this
                    Loyalty Prefix (e.g., VIP2018100000).
                </ReactTooltip>
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <SelectInput source="active" label="Card Configuration is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'},
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this series of Loyalty Account IDs may be applied on the POS System.
                    <br/>
                    If <em>Inactive</em>, this series of Loyalty Account IDs may not be applied on the POS System.
                    <br/><br/>
                    Setting a Configuration to inactive allows you to create Loyalty Accounts that, for instance,
                    are valid for a limited time.
                </ReactTooltip>
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the VIP Card Configurations List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Loyalty/vipCardConfig.js